import { useState } from "react";
import { LegalComplianceStep } from "@/features/legal/components/LegalComplianceStep";
import { PostLoginOnboardingLayout } from "@/features/legal/components/onboarding/PostLoginOnboardingLayout";
import { PreferencesStep } from "@/features/legal/components/onboarding/PreferencesStep";
import { ReadyStep } from "@/features/legal/components/onboarding/ReadyStep";
import { PREFERENCES_COPY } from "@/features/legal/config/onboarding.config";
import type { LegalCompliance } from "@/features/legal/types/legalCompliance.type";
import type { OnboardingPreferences } from "@/features/legal/types/onboarding.type";

type LegalOnboardingStep = "legal" | "preferences" | "ready";

type LegalComplianceOnboardingProps = {
  hasSecurityStep: boolean
  initialPreferences: OnboardingPreferences
  cellPhoneNumber: string | null
  onCompleted: (savedPreferences?: OnboardingPreferences) => void
  onRefused: () => void
  onLogout: () => void
};

// Conditions → optional preferences → recap. The login chain stays paused
// until the recap is left: accepting alone does not open the space.
export const LegalComplianceOnboarding = ({
  hasSecurityStep, initialPreferences, cellPhoneNumber, onCompleted, onRefused, onLogout,
}: LegalComplianceOnboardingProps) => {
  const [step, setStep] = useState<LegalOnboardingStep>("legal");
  const [acceptedVersionLabel, setAcceptedVersionLabel] = useState<string | null>(null);
  const [preferences, setPreferences] = useState<OnboardingPreferences>(initialPreferences);

  const handleAccepted = (legalCompliance: LegalCompliance) => {
    setAcceptedVersionLabel(legalCompliance.version_label);
    setStep("preferences");
  };

  const handlePreferencesSaved = (savedPreferences: OnboardingPreferences) => {
    setPreferences(savedPreferences);
    setStep("ready");
  };

  return (
    <PostLoginOnboardingLayout
      currentStep={step}
      hasSecurityStep={hasSecurityStep}
      hasLegalSteps
      captionSuffix={step === "preferences" ? PREFERENCES_COPY.STEP_CAPTION : undefined}
    >
      {step === "legal" && (
        <LegalComplianceStep
          onAccepted={handleAccepted}
          onNothingToAccept={onCompleted}
          onRefused={onRefused}
          onLogout={onLogout}
        />
      )}
      {step === "preferences" && (
        <PreferencesStep initialPreferences={preferences} cellPhoneNumber={cellPhoneNumber} onSaved={handlePreferencesSaved} />
      )}
      {step === "ready" && acceptedVersionLabel && (
        <ReadyStep
          versionLabel={acceptedVersionLabel}
          preferences={preferences}
          onEnter={() => onCompleted(preferences)}
          onEditPreferences={() => setStep("preferences")}
        />
      )}
    </PostLoginOnboardingLayout>
  );
};

LegalComplianceOnboarding.displayName = "LegalComplianceOnboarding";
